import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, GraduationCap, Compass, MessageSquare, Loader2, ArrowLeft, Minus, Pencil, Check, X, ListChecks, History } from "lucide-react";
import client, { errText } from "../lib/api";
import { guardCrisis, raiseCrisis } from "../lib/safety";

const LIKERT_LABELS = ["Not at all", "A little", "Somewhat", "Mostly", "Very much"];

/**
 * Your answers. A calm look back at what you told Klair: your sign-up
 * intake, your intake answers, your assessment answers, and the
 * conversation. Owner only. Skipped items are shown plainly, never
 * hidden.
 *
 * KLA-012: answers are editable. Every edit is versioned and
 * timestamped server-side with the original kept, and your read
 * regenerates itself transparently after a change.
 */
export default function AnswersScreen({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedNote, setSavedNote] = useState("");

  const refresh = useCallback(() => {
    return client.get("/profile/answers")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const onSaved = useCallback((res) => {
    if (res?.support_notice) raiseCrisis("server");
    setSavedNote(
      res?.regenerated
        ? "Saved. Your read is updating itself with this change."
        : "Saved and versioned. Your read refreshes on your next visit."
    );
    setTimeout(() => setSavedNote(""), 6000);
    refresh();
  }, [refresh]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-5 pt-16 text-center"><Loader2 size={22} className="animate-spin mx-auto" style={{ color: "hsl(var(--klareet-muted))" }} /></div>;
  }

  const onb = data?.onboarding || {};
  const edu = onb.education || [];
  const chips = onb.experience_chips || [];
  const intake = data?.intake_answers || [];
  const assessment = data?.assessment_answers || [];
  const convo = data?.conversation || [];

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <Link to={`${base}/account`} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs mb-3" data-testid="answers-back">
        <ArrowLeft size={13} /> Profile
      </Link>
      <div className="k-label mb-2 flex items-center gap-2"><ClipboardList size={14} /> your answers</div>
      <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="answers-title">
        What you told Klair.
      </h1>
      <p className="k-hero-sub mb-2">A private look back at your own words. This is only for you.</p>
      <p className="k-quiet !text-sm mb-6">You can edit any answer. Every version is kept, and your read updates itself after a change.</p>

      {savedNote && (
        <div className="k-card mb-5 !py-3 flex items-center gap-2" data-testid="answers-saved-note">
          <Check size={15} style={{ color: "hsl(var(--klareet-ink))" }} />
          <span className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{savedNote}</span>
        </div>
      )}

      {/* About you (onboarding) */}
      <div className="k-card mb-5" data-testid="answers-about">
        <div className="k-label mb-3 flex items-center gap-2"><GraduationCap size={14} /> about you</div>
        <dl className="space-y-3">
          <Field label="Education">
            {edu.length ? (
              <ul className="space-y-1">
                {edu.map((e, i) => (
                  <li key={i} className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`answers-edu-${i}`}>
                    {[e.programme, e.institution].filter(Boolean).join(" · ")}
                    {e.year ? <span className="k-quiet"> ({e.year})</span> : null}
                    {e.status && e.status !== "current" ? <span className="k-quiet"> ({e.status})</span> : null}
                  </li>
                ))}
              </ul>
            ) : <Empty />}
          </Field>
          <Field label="What you have done">
            {chips.length ? (
              <div className="flex flex-wrap gap-2">
                {chips.map((c, i) => <span key={i} className="k-pill" data-testid={`answers-chip-${i}`}>{c}</span>)}
              </div>
            ) : <Empty />}
          </Field>
          <Field label="Your goal">{onb.goal ? <span className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))" }}>{onb.goal}</span> : <Empty />}</Field>
          {onb.gender ? <Field label="Gender">{onb.gender}</Field> : null}
        </dl>
        <p className="k-quiet !text-xs mt-3">Education, experience and goal are edited from your Profile.</p>
      </div>

      {/* Intake answers (editable text) */}
      {intake.length > 0 && (
        <div className="k-card mb-5" data-testid="answers-intake">
          <div className="k-label mb-3 flex items-center gap-2"><Compass size={14} /> your intake</div>
          <div className="space-y-4">
            {intake.map((a, i) => (
              <EditableQA
                key={a.key || i}
                answerKey={a.key}
                prompt={a.prompt}
                answer={a.answer}
                skipped={a.skipped}
                edited={a.edited}
                kind="text"
                editable={!!a.key}
                onSaved={onSaved}
                tid={`answers-intake-${i}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Assessment answers (editable picks + narrative) */}
      {assessment.length > 0 && (
        <div className="k-card mb-5" data-testid="answers-assessment">
          <div className="k-label mb-3 flex items-center gap-2"><ListChecks size={14} /> your assessment answers</div>
          <div className="space-y-4">
            {assessment.map((a, i) => (
              <EditableQA
                key={a.key || i}
                answerKey={a.key}
                prompt={a.stem}
                answer={a.answer_kind === "text"
                  ? String(a.value ?? "")
                  : (((Array.isArray(a.options) && a.options.length >= 2 ? a.options : LIKERT_LABELS)[(a.value || 0) - 1]) || String(a.value ?? ""))}
                rawValue={a.value}
                skipped={a.value === null || a.value === undefined || a.value === ""}
                edited={a.edited}
                kind={a.answer_kind === "text" ? "text" : "value"}
                options={a.options}
                editable
                onSaved={onSaved}
                tid={`answers-assess-${i}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Conversation (the transcript stays as it happened) */}
      {convo.length > 0 ? (
        <div className="k-card" data-testid="answers-conversation">
          <div className="k-label mb-3 flex items-center gap-2"><MessageSquare size={14} /> your conversation</div>
          <p className="k-quiet !text-xs mb-3">The transcript stays as it happened; edits above change what your read is built from.</p>
          <div className="space-y-4">
            {convo.map((a, i) => <QA key={i} prompt={a.prompt} answer={a.answer} skipped={a.skipped} tid={`answers-convo-${i}`} />)}
          </div>
        </div>
      ) : (
        !data?.has_assessment && (
          <div className="k-card text-center py-10" data-testid="answers-none">
            <p className="k-quiet">You have not finished a conversation with Klair yet. When you do, your answers show up here.</p>
            <Link to={`${base}/klair`} className="k-btn k-btn-primary mt-4 inline-flex" data-testid="answers-start-cta">Talk to Klair</Link>
          </div>
        )
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="k-quiet !text-xs uppercase tracking-wide mb-1">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function QA({ prompt, answer, skipped, tid }) {
  return (
    <div data-testid={tid}>
      {prompt ? <div className="k-quiet !text-sm mb-1">{prompt}</div> : null}
      {skipped ? (
        <div className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-muted))" }}>
          <Minus size={13} /> {answer}
        </div>
      ) : (
        <div className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>{answer}</div>
      )}
    </div>
  );
}

/**
 * One question-answer row with an in-place editor. kind "text" edits a
 * textarea; kind "value" edits a 1..5 pick. Saving PUTs the edit; the
 * server versions it, keeps the original, and regenerates the read.
 */
function EditableQA({ answerKey, prompt, answer, rawValue, skipped, edited, kind, options, editable, onSaved, tid }) {
  // KLA-028: a tap/puzzle answer edits and displays with the item's OWN
  // option labels; likert keeps the five agreement words.
  const pillLabels = Array.isArray(options) && options.length >= 2 ? options : LIKERT_LABELS;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const begin = () => {
    setErr("");
    if (kind === "text") setText(skipped ? "" : String(answer || ""));
    else setPick(typeof rawValue === "number" ? rawValue : null);
    setEditing(true);
  };

  const save = async () => {
    setErr("");
    const body = {};
    if (kind === "text") {
      const t = text.trim();
      if (!t) { setErr("Say it in a few words first."); return; }
      if (guardCrisis(t)) return;
      body.text = t;
    } else {
      if (!pick) { setErr("Pick one of the options first."); return; }
      body.value = pick;
    }
    setBusy(true);
    try {
      const { data } = await client.put(`/profile/answers/${encodeURIComponent(answerKey)}`, body);
      setEditing(false);
      onSaved && onSaved(data);
    } catch (e) {
      setErr(errText(e, "That edit did not save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={tid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {prompt ? (
            <div className="mb-1.5">
              <div className="k-label !text-[10px] mb-0.5">Klair asked</div>
              <div className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.45 }}>{prompt}</div>
            </div>
          ) : null}
          {!editing && (
            skipped ? (
              <div className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-muted))" }}>
                <Minus size={13} /> {answer || "You skipped this."}
              </div>
            ) : (
              <div>
                <div className="k-label !text-[10px] mb-1">you said</div>
                {kind === "text" ? (
                  <div className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>{answer}</div>
                ) : (
                  <span className="k-pill k-pill-on !cursor-default" data-testid={`${tid}-answer-pill`}>{answer}</span>
                )}
              </div>
            )
          )}
          {!editing && edited?.revision ? (
            <div className="k-quiet !text-xs mt-1 flex items-center gap-1" data-testid={`${tid}-edited`}>
              <History size={11} /> edited · v{edited.revision}{edited.edited_at ? ` · ${String(edited.edited_at).slice(0, 10)}` : ""}
            </div>
          ) : null}
        </div>
        {editable && !editing && (
          <button onClick={begin} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs shrink-0" data-testid={`${tid}-edit`} aria-label="edit this answer">
            <Pencil size={12} /> edit
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-2" data-testid={`${tid}-editor`}>
          {kind === "text" ? (
            <textarea
              className="k-input resize-none klareet-serif w-full"
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
              data-testid={`${tid}-input`}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {pillLabels.map((label, i) => (
                <button
                  key={i}
                  onClick={() => setPick(i + 1)}
                  disabled={busy}
                  aria-pressed={pick === i + 1}
                  className={`k-pill ${pick === i + 1 ? "k-pill-on" : ""}`}
                  data-testid={`${tid}-pick-${i + 1}`}
                >{label}</button>
              ))}
            </div>
          )}
          {err && <p className="text-sm mt-2" style={{ color: "hsl(var(--destructive))" }}>{err}</p>}
          <div className="flex items-center gap-2 mt-2">
            <button onClick={save} disabled={busy} className="k-btn k-btn-primary !py-1.5 !px-3 !text-sm" data-testid={`${tid}-save`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
            </button>
            <button onClick={() => setEditing(false)} disabled={busy} className="k-btn k-btn-ghost !py-1.5 !px-3 !text-sm" data-testid={`${tid}-cancel`}>
              <X size={13} /> Cancel
            </button>
            <span className="k-quiet !text-xs">the original stays saved; your read updates itself</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Empty() {
  return <span className="k-quiet !text-sm">Not added.</span>;
}
